import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import useContentUpdates from '../hooks/useContentUpdates';
import { useContext } from 'react';
import { CartContext } from '../contexts/CartContext';

function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useContext(CartContext);
  const [isProcessing, setIsProcessing] = useState(true);

  // Get order info from URL params (PayPal returns these)
  const orderId = searchParams.get('orderId') || searchParams.get('order_id');
  const paymentId = searchParams.get('paymentId') || searchParams.get('token');

  useEffect(() => {
    const handlePaymentSuccess = async () => {
      try {
        // Clear the cart since payment was successful
        await clearCart();
        toast.success('Payment completed successfully!');

        // If we have an orderId, redirect to order confirmation
        if (orderId) {
          setTimeout(() => {
            navigate(`/order-confirmation/${orderId}`);
          }, 2000);
        }
      } catch (error) {
        console.error('[CheckoutSuccess] Error:', error);
      } finally {
        setIsProcessing(false);
      }
    };

    handlePaymentSuccess();
  }, [orderId, clearCart, navigate]);

  // Initialize scripts
  useEffect(() => {
    const handleWindowLoad = () => {
      if (window.$ && window.$('.loader1').length > 0) {
        window.$(".loader1").fadeOut(800);
        window.$('.side-menu').removeClass('hidden');
      }
    };

    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    if (window.WOW) {
      new window.WOW().init();
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
    };
  }, []);


  const [backgroundUrl, setBackgroundUrl] = useState(null);

  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }
    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      }
    } catch (err) {
      console.error('[CheckoutSuccess] Error fetching background:', err);
    }
  }, []);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  useContentUpdates({
    onPageBackgroundUpdated: (page) => {
      if (page === 'product-listing') {
        fetchBackground();
      }
    }
  });

  return (
    <Layout>
      {/* Parallax Banner */}
      {backgroundUrl && (
        <ParallaxBanner
          imageUrl={backgroundUrl}
          speed={0.6}
          offset={0}
          className="paralax-section-slide-data"
        />
      )}

      {/* Success Section */}
      <div className="about_content">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5">
              <div className="success-icon mb-4 wow fadeIn" data-wow-duration="1s">
                <i className="fas fa-check-circle" style={{
                  fontSize: '100px',
                  color: '#28a745'
                }}></i>
              </div>
              <h1 className="heading mb-3 wow fadeIn" data-wow-duration="1.2s">Payment Successful!</h1>
              <p className="para_text mb-4 wow fadeIn" data-wow-duration="1.4s">
                Thank you for your purchase. Your payment has been processed successfully.
              </p>

              {isProcessing ? (
                <div className="wow fadeIn" data-wow-duration="1.6s">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p>Redirecting to your order confirmation...</p>
                </div>
              ) : (
                <div className="wow fadeIn" data-wow-duration="1.6s">
                  {orderId ? (
                    <Link
                      to={`/order-confirmation/${orderId}`}
                      className="btn pink-gradient-btn-into-black"
                    >
                      View Order Details
                    </Link>
                  ) : (
                    <Link to="/" className="btn pink-gradient-btn-into-black">
                      Continue Shopping
                    </Link>
                  )}
                </div>
              )}

              {paymentId && (
                <p className="text-muted mt-4 small">
                  Payment Reference: {paymentId}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default CheckoutSuccess;
